import React, { useState, useEffect, useCallback } from 'react';
import { apiService, RedemptionItem } from '../services/api';
import './RedemptionsView.css';

const RedemptionsView: React.FC = () => {
  const [redemptions, setRedemptions] = useState<RedemptionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize] = useState(20);

  const totalPages = Math.ceil(totalItems / pageSize);

  const fetchRedemptions = useCallback(async () => {
      setLoading(true);
    setError(null);
      try {
      const data = await apiService.getRedemptions(currentPage, pageSize);
      setRedemptions(data.items || []);
      setTotalItems(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch redemptions:', err);
      setError('Failed to load redemptions. Please try again.');
        setRedemptions([]);
      } finally {
        setLoading(false);
      }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchRedemptions();
  }, [fetchRedemptions]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };

  if (loading && redemptions.length === 0) {
    return (
      <div className="redemptions-view">
        <div className="info-card">
          <div className="info-card-icon">✅</div>
          <div className="info-card-content">
            <h3>Redemptions</h3>
            <p>Track which customers have redeemed your offerings and when.</p>
          </div>
        </div>
        <div className="loading-state">Loading redemptions...</div>
      </div>
    );
  }

  return (
    <div className="redemptions-view">
      <div className="info-card">
        <div className="info-card-icon">✅</div>
        <div className="info-card-content">
          <h3>Redemptions</h3>
          <p>Track which customers have redeemed your offerings and when.</p>
        </div>
        <span className="record-count">{totalItems} total</span>
      </div>

      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchRedemptions} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {!error && redemptions.length === 0 ? (
        <div className="empty-state">
          <p>No redemptions yet. Users will appear here when they redeem your offerings.</p>
        </div>
      ) : !error && (
        <>
        <div className="redemptions-table-container">
          <table className="redemptions-table">
            <thead>
              <tr>
                  <th>User</th>
                <th>Offering</th>
                <th>Redeemed At</th>
              </tr>
            </thead>
            <tbody>
                {redemptions.map((redemption, index) => {
                  const { date, time } = formatDate(redemption.redeemed_at);
                return (
                    <tr key={`${redemption.username}-${redemption.redeemed_at}-${index}`}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                            {redemption.username.charAt(0).toUpperCase()}
                        </div>
                          <span>{redemption.username}</span>
                      </div>
                    </td>
                    <td>
                        <span className="offering-tag">{redemption.offering_name}</span>
                    </td>
                    <td>
                      <div className="date-cell">
                        <div>{date}</div>
                        <div className="time-text">{time}</div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              <div className="pagination-numbers">
                {getPageNumbers().map((page, index) => (
                  typeof page === 'number' ? (
                    <button
                      key={index}
                      className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ) : (
                    <span key={index} className="pagination-ellipsis">{page}</span>
                  )
                ))}
              </div>
              
              <button
                className="pagination-button"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}

          <div className="pagination-info">
            Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} redemptions
          </div>
        </>
      )}
    </div>
  );
};

export default RedemptionsView;
